import { dangerouslyInjectHTML } from '@visiba/react-std';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

type Mirror = 'Vertical' | 'Horizontal' | true | null;

export interface VisibaCortexBaseIconProps {
	viewBox?: string;
	rotation?: number;
	mirror?: Mirror;
	center?: boolean;
	dangerouslySetInnerHTML?: ReturnType<typeof dangerouslyInjectHTML>;
	children?: ReactNode;
}

const elements = {
	svg: styled.svg<{ rotation: number; mirror: Mirror; center: boolean }>`
		width: 1em;
		height: 1em;
		display: inline-block;
		vertical-align: text-top;
		pointer-events: none;
		fill: currentColor;
		stroke: currentColor;
		flex-shrink: 0;

		${({ rotation, mirror, center }): string | null => {
			// Needs to concat "transform" so they won't interfere with each other
			let transformBuilder = '';

			if (rotation !== 0) {
				transformBuilder += `rotate(${rotation}deg) `;
			}

			if (mirror !== null) {
				const horizontal: boolean = mirror === 'Horizontal' || mirror === true || false;
				const vertical: boolean = mirror === 'Vertical' || mirror === true || false;

				if (horizontal) {
					transformBuilder += 'scaleX(-1) ';
				}

				if (vertical) {
					transformBuilder += 'scaleY(-1) ';
				}
			}

			if (center) {
				transformBuilder += 'translateY(50%)';

				return `
					position: relative;
					top: 50%;
					transform: ${transformBuilder.trim()};
					transform-origin: 50% 100%;
					vertical-align: super;
				`;
			} else if (transformBuilder !== '') {
				return `transform: ${transformBuilder.trim()};`;
			}

			return null;
		}}
	`,
};

/**
 * # cortex:react-ui-std:comp:BaseIcon
 */
export const BaseIcon: React.FC<VisibaCortexBaseIconProps> = ({
	children,
	viewBox,
	rotation,
	mirror,
	center,
	dangerouslySetInnerHTML,
}) => {
	return (
		<elements.svg
			data-testid='svg'
			xmlns='http://www.w3.org/2000/svg'
			viewBox={viewBox ?? '0 0 512 512'}
			rotation={rotation ?? 0}
			mirror={mirror ?? null}
			center={center ?? false}
			// biome-ignore lint/security/noDangerouslySetInnerHtmlWithChildren: suppressed due to migration - should be fixed
			dangerouslySetInnerHTML={dangerouslySetInnerHTML}
		>
			{children}
		</elements.svg>
	);
};
